import { markRaw } from 'vue'

import { RootState } from '@/store'
import { PermissionState } from './state'
import { Mutations } from './mutations'
import { PermissionMutationType } from './mutation-types'
import { PermissionActionType } from './action-types'
import { getMenuList, getButtonsByMenuId } from '@/apis/user'
import Layout from '@/layout/Index.vue'
import ParentView from '@/components/ParentView/index.vue'

import { ActionTree, ActionContext } from 'vuex'

type AugmentedActionContext = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1],
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<PermissionState, RootState>, 'commit'>

export interface Actions {
  [PermissionActionType.ACTION_SET_ROUTES](
    { commit }: AugmentedActionContext,
    roles: string[]): void;

  [PermissionActionType.ACTION_SET_PERMISSIONS](
    { commit }: AugmentedActionContext, menuId: number
  ): void;
}
export interface Routes {
  path: string;
  name: string;
  component: any;
  children: Array<Routes>;
  meta: {
    title: string;
    icon: string;
    menuId: number;
  };
}

// 使用 require.context 预扫描所有视图组件，解决 Webpack 5 动态路径问题
const views = require.context('@/views', true, /\.vue$/)
const viewsMap = new Map()
views.keys().forEach(key => {
  // key 格式如 './dashboard/Index.vue'
  const path = key.replace(/^\.\//, '').replace(/\.vue$/, '')
  viewsMap.set(path, key)
})

const componentCacheMap = new Map<string, any>()

export const loadView = (view: any, isMenu?: boolean, routeName?: string) => {
  if (isMenu) return markRaw(ParentView)

  // 规范化路径：去除首尾斜杠和 .vue 后缀
  let path = view.startsWith('/') ? view.substring(1) : view
  path = path.replace(/\.vue$/, '')

  const componentKey = viewsMap.get(path)
  if (!componentKey) {
    console.error('未在 views 目录中找到组件文件:', path)
    return () => Promise.resolve(markRaw(require('@/views/404/Index.vue').default))
  }

  const cacheKey = `${path}__${routeName || ''}`
  if (componentCacheMap.has(cacheKey)) {
    return componentCacheMap.get(cacheKey)
  }

  const loader = () => {
    return Promise.resolve().then(() => {
      const moduleExport = views(componentKey)
      const rawComp = moduleExport.default || moduleExport
      if (!routeName) {
        return markRaw(rawComp)
      }
      if (rawComp.name === routeName) {
        return markRaw(rawComp)
      }
      // 动态合成带有目标 routeName 的组件定义，确保 KeepAlive include 100% 命中
      const wrapped = Object.assign({}, rawComp, { name: routeName })
      return markRaw(wrapped)
    })
  }

  componentCacheMap.set(cacheKey, loader)
  return loader
}

export function getMenuRoutes(routes: any, data: any, step?: number, parents: any[] = []) {
  // 过滤已下线的重复菜单（访问日志与操作日志功能重复，统一保留操作日志）
  data = data.filter((item: any) => {
    const isAccessLog = item.path === '/system/userLog/accessLog/Index' ||
                        item.path === 'system/userLog/accessLog/Index' ||
                        item.name === '访问日志'
    return !isAccessLog
  })
  data = data.sort((a: any, b: any) => {
    return a.order - b.order
  })
  data.forEach((route: any) => {
    const _tempIcon = step === 1 ? 'icon-tubiao-06' : ''
    route.path = route.path ? route.path : '/404/Index'
    const isMenu = Boolean(route.children && route.children.length)
    const name = route.path.replace(/\//g, '_').replace(/^_/, '')
    const routeName = name ? name.substring(0, 1).toUpperCase() + name.toLowerCase().substring(1) : ''
    const currentPath = route.path.startsWith('/') ? route.path : `/${route.path}`
    const currentBreadcrumb = [...parents, { path: currentPath, title: route.name }]

    const newRouters: any = {
      path: currentPath,
      name: routeName,
      component: loadView(route.path, isMenu, routeName) || ParentView,
      children: [],
      meta: {
        title: route.name,
        icon: route.icon || _tempIcon,
        menuId: route.id,
        notCache: false,
        noCache: false,
        breadcrumbHierarchy: currentBreadcrumb
      }
    }
    if (route.children && route.children.length) {
      getMenuRoutes(newRouters.children, route.children, (step || 0) + 1, currentBreadcrumb)
    }

    routes.push(newRouters)
  })
}

/**
 * 将层级树拍平为单层叶子路由，挂载到 LayoutRoot 下，消除多层 ParentView 对 KeepAlive 的阻断
 */
export function flattenRoutes(tree: any[]): any[] {
  let flat: any[] = []
  tree.forEach((item) => {
    if (item.children && item.children.length > 0) {
      flat = flat.concat(flattenRoutes(item.children))
    } else {
      flat.push({
        path: item.path,
        name: item.name,
        component: item.component,
        meta: { ...item.meta }
      })
    }
  })
  return flat
}

export const actions: ActionTree<PermissionState, RootState> & Actions = {

  async [PermissionActionType.ACTION_SET_ROUTES](
    { commit }: AugmentedActionContext) {
    try {
      const accessedRoutes = [{
        path: '/',
        name: 'LayoutRoot',
        component: markRaw(Layout),
        children: []
      }] as any

      await getMenuList().then((res: any) => {
        const sidebarTree: any[] = []
        getMenuRoutes(sidebarTree, res, 1, [])

        // 扁平化叶子节点供 Vue Router 单层注册与 KeepAlive 直连
        const flatChildren = flattenRoutes(sidebarTree)
        accessedRoutes[0].children = flatChildren

        let _route = ''
        if (flatChildren && flatChildren.length) {
          _route = flatChildren[0].path
        } else {
          _route = accessedRoutes[0].path
        }
        accessedRoutes.push({ path: '/:pathMatch(.*)*', name: 'PathMatch', redirect: _route })

        commit(PermissionMutationType.SET_ROUTES, {
          routes: accessedRoutes,
          dynamicRoutes: accessedRoutes,
          sidebarRoutes: sidebarTree
        })
        return accessedRoutes
      })
    } catch (error) {
      console.error('ACTION_SET_ROUTES 发生异常:', error); throw error
    }
  },

  async [PermissionActionType.ACTION_SET_PERMISSIONS](
    { commit }: AugmentedActionContext,
    menuId: number
  ) {
    await getButtonsByMenuId(menuId).then((res: any) => {
      commit(PermissionMutationType.SET_PERMISSIONS, res)
    })
  }
}
